/**
 * AppStoreSubscriptionsPage — 3-level expandable view of the latest
 * `subscription` snapshot from the new schema.
 *
 *   App
 *     └─ Subscription type (name + duration)
 *           └─ Country (active subscribers + price)
 *
 * Plus a hero card up top showing the estimated monthly developer
 * proceeds in TRY.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';

import { subscriptionsApi, refreshCache } from '../services/mrdsApi';
import {
  PageHeader,
  MetricCard,
  EmptyState,
  PlatformChip,
} from '../components/ui';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR'));

const fmtMoney = (n, currency = '') =>
  n == null
    ? '—'
    : `${Number(n).toLocaleString('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${currency}`.trim();

/**
 * Convert one billing cycle into "per calendar month".
 * Apple's `Standard Subscription Duration` arrives as "1 week",
 * "1 month", "3 months", "1 year" etc. Free trials (no recurring
 * billing yet) collapse to 0 so they don't inflate the estimate.
 */
const monthlyFactor = (duration) => {
  const d = (duration || '').toLowerCase().trim();
  if (!d) return 0;
  if (d.includes('free') || d.includes('trial')) return 0;
  if (d.includes('week')) return 52 / 12;
  if (d.includes('year')) return 1 / 12;
  const match = d.match(/^(\d+)\s*month/);
  if (match) {
    const n = Number(match[1]);
    return n > 0 ? 1 / n : 0;
  }
  return 1;
};

export default function AppStoreSubscriptionsPage() {
  const [rows, setRows] = useState([]);
  const [asOfDate, setAsOfDate] = useState(null);
  const [warning, setWarning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    subscriptionsApi
      .getLatest({ maxAgeDays: 7 })
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows || []);
        setAsOfDate(res.asOfDate || null);
        setWarning(res.warning || null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError('Subscription verileri yüklenirken hata oluştu.');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const totals = useMemo(() => {
    let appCount = 0;
    let subCount = 0;
    let countryCount = 0;
    let totalActive = 0;
    let estMonthlyTRY = 0;
    const perApp = [];
    rows.forEach((app) => {
      appCount += 1;
      totalActive += app.total_active_subscribers || 0;
      let appMonthlyTRY = 0;
      app.by_subscription_type.forEach((st) => {
        subCount += 1;
        countryCount += st.by_country.length;
        const factor = monthlyFactor(st.duration);
        st.by_country.forEach((c) => {
          if ((c.currency || '').toUpperCase() !== 'TRY') return;
          const proceedsPerCycle = Number(c.developer_proceeds) || 0;
          const subs = Number(c.active_subscribers) || 0;
          const contrib = proceedsPerCycle * subs * factor;
          estMonthlyTRY += contrib;
          appMonthlyTRY += contrib;
        });
      });
      perApp.push({ name: app.app_name, estMonthlyTRY: appMonthlyTRY });
    });
    perApp.sort((a, b) => b.estMonthlyTRY - a.estMonthlyTRY);
    return { appCount, subCount, countryCount, totalActive, estMonthlyTRY, perApp };
  }, [rows]);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <PageHeader
        title="App Store Subscriptions"
        subtitle="Latest active-subscriber snapshot, broken down by app, subscription type and country."
        rightSlot={
          <Stack direction="row" spacing={1} alignItems="center">
            {asOfDate && (
              <Chip
                label={`As of ${asOfDate}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            <MuiTooltip title="Refresh">
              <IconButton
                onClick={() => {
                  refreshCache();
                  setReloadKey((k) => k + 1);
                }}
              >
                <RefreshIcon />
              </IconButton>
            </MuiTooltip>
          </Stack>
        }
      />

      {warning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warning}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Hero: estimated monthly developer proceeds in TRY */}
      <Box sx={{ mb: totals.perApp.length > 0 ? 0 : 3 }}>
        <MetricCard
          variant="hero"
          label="Estimated Monthly Proceeds (TRY)"
          value={fmtMoney(totals.estMonthlyTRY, 'TRY')}
          hint="Active subscribers × developer proceeds, normalised to one calendar month. TRY-priced subscriptions only."
        />
      </Box>

      {/* Per-app breakdown of the monthly estimate */}
      {totals.perApp.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>App</TableCell>
                <TableCell align="right">Est. Monthly Proceeds (TRY)</TableCell>
                <TableCell align="right">Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {totals.perApp.map((a) => (
                <TableRow key={a.name}>
                  <TableCell>{a.name}</TableCell>
                  <TableCell align="right">{fmtMoney(a.estMonthlyTRY, 'TRY')}</TableCell>
                  <TableCell align="right">
                    {totals.estMonthlyTRY > 0
                      ? `${((a.estMonthlyTRY / totals.estMonthlyTRY) * 100).toFixed(1)}%`
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                <TableCell>Total</TableCell>
                <TableCell align="right">{fmtMoney(totals.estMonthlyTRY, 'TRY')}</TableCell>
                <TableCell align="right">100%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <MetricCard label="Apps with subs" value={fmt(totals.appCount)} accent="#3357FF" />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            label="Subscription products"
            value={fmt(totals.subCount)}
            accent="#16A34A"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            label="Country lines"
            value={fmt(totals.countryCount)}
            accent="#D97706"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            label="Active subscribers"
            value={fmt(totals.totalActive)}
            accent="#9333EA"
          />
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <EmptyState title="No subscription rows for the latest snapshot." />
      ) : (
        rows.map((app) => (
          <Accordion key={`${app.app_name}-${app.platform}`} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
                <PlatformChip platform={app.platform} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {app.app_name}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {app.by_subscription_type.length} sub product(s)
                </Typography>
                <Typography variant="h6" sx={{ minWidth: 80, textAlign: 'right' }}>
                  {fmt(app.total_active_subscribers)}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {app.by_subscription_type.map((st) => (
                <Accordion
                  key={`${app.app_name}-${st.name}-${st.duration}`}
                  sx={{
                    border: 'none',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0,
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {st.name}
                      </Typography>
                      <Chip label={st.duration} size="small" variant="outlined" />
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {st.by_country.length} country line(s)
                      </Typography>
                      <Typography variant="subtitle1" sx={{ minWidth: 80, textAlign: 'right' }}>
                        {fmt(st.active_subscribers)}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Country</TableCell>
                            <TableCell align="right">Active subs</TableCell>
                            <TableCell align="right">Customer price</TableCell>
                            <TableCell align="right">Developer proceeds</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {st.by_country.map((c, i) => (
                            <TableRow key={`${c.country}-${i}`}>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="body2">{c.country}</Typography>
                                  <Chip label={c.currency} size="small" variant="outlined" />
                                </Stack>
                              </TableCell>
                              <TableCell align="right">{fmt(c.active_subscribers)}</TableCell>
                              <TableCell align="right">{fmtMoney(c.customer_price, c.currency)}</TableCell>
                              <TableCell align="right">{fmtMoney(c.developer_proceeds, c.currency)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Container>
  );
}
